/// <reference path="../../../typedefs/dash.js" />

import { curry } from 'ramda'
import { insertIf } from 'utils/types'
import { RulesByModelCode } from './rulesByModelCode'
import {
  typeRulesSelector,
  rulesSelector,
  locationRulesSelector,
} from './ruleSelectors'

/**
 * @typedef {object} CreateRulesArg
 * @property {string} modelCodeRules
 * @property {string} componentDetail
 * @property {string} locationType
 * @property {string} componentType
 * @property {string[]} databookCodes
 * @property {string} componentLocation
 * @property {boolean} isPreProduction
 */

/**
 * This function builds all the rules for a dash model code and other data like databook codes.
 * It uses the rule type selectors and rule export object keys to find the correct rule.
 * @param {CreateRulesArg} params
 * @returns {Rule[]} array of rules
 */
export const createRules = ({
  modelCodeRules,
  componentDetail,
  componentType,
  locationType,
  databookCodes,
  componentLocation,
  isPreProduction,
}) => {
  const detailRules = rulesSelector(modelCodeRules, 'detail', componentDetail)

  const typeRules = typeRulesSelector(modelCodeRules, 'type', [
    ...insertIf(isPreProduction, 'preProduction'),
    locationType,
    componentType,
  ])

  const databookCodeRules = databookCodes.map((dbCode) =>
    rulesSelector(modelCodeRules, 'databookCode', dbCode)
  )

  const locationRules = locationRulesSelector(modelCodeRules, 'location', [
    componentLocation,
    componentType,
  ])

  const parentRules = rulesSelector(modelCodeRules, 'parent')

  const panelNumberRules = rulesSelector(modelCodeRules, 'panelNumber')

  const rules = [
    ...detailRules,
    ...typeRules,
    ...databookCodeRules,
    ...locationRules,
    ...parentRules,
    ...panelNumberRules,
  ].flat()

  return rules
}

/**
 * A function to find the first rule to return false, which then returns the rule.
 * No found rule returns undefined which is a positive result. No rule blocks movement.
 * @param {{
 *  rule: func
 *  name: string
 * }} rules
 * @param {object} state
 * @param {object} location
 * @param {object} component
 * @return {Rule | undefined}
 */
export const findNotAllowedRule = curry((rules, state, component, location) =>
  rules.find(({ rule, name }) => {
    try {
      const result = rule(state)(component, location)
      return result === false
    } catch (e) {
      console.error(e, name)
    }
    return false
  })
)

/**
 * Get all the rules by selector keys for dash model code.
 * @param {*} modelCode
 * @returns {{Rule}} an object of rules by selector key
 */
export const getDashboardTypeRules = (modelCode) => RulesByModelCode[modelCode]

/**
 * The allowed locations rule engine creates the needed context state,
 * gets the component object and location object. Gathers rules, runs the evaluator,
 * return the allowed object. If not allowed, the rule name that falses is also returned.
 * @param {*} state
 * @param {*} component
 * @param {*} location
 * @return {{allowed: boolean} || {allowed: boolean, name: string}}
 */
export const allowedLocationsRulesEngine = curry(
  (state, component, location) => {
    const { dashboardType = '', databookCodes = [], isPreProduction } = state

    const {
      type: componentType = '',
      locationType = '',
      locationDetail: componentDetail = '',
      location: componentLocation = '',
    } = component

    const { detail: locationDetail } = location

    const modelCodeRules = getDashboardTypeRules(dashboardType)
    const rules = createRules({
      modelCodeRules,
      componentDetail,
      componentType,
      locationDetail,
      locationType,
      databookCodes,
      componentLocation,
      isPreProduction,
    })
    const notAllowedRule = findNotAllowedRule(rules, state, component, location)

    if (notAllowedRule)
      return {
        allowed: false,
        rule: notAllowedRule.name,
      }

    return {
      allowed: true,
    }
  }
)
